/**
 * Filtros operacionais da lista ecommerce (cards agrupados).
 * Classificação via resolveTransactionOpState (1 linha = 1 transação).
 * @module patientListFilters
 */

import type { Patient } from "fhir/r5";
import { ecommercePatientReference } from "./patientEcommerceOpState";
import type { DocumentTransactionRow, TransactionOpState } from "./documentTransactions";

export { ecommercePatientReference };

export type PatientOpFilter =
  | "docs_pending"
  | "docs_rejected"
  | "docs_to_approve"
  | "awaiting_release"
  | "docs_released"
  | "purchase_completed"
  | "all";

export const PATIENT_OP_FILTER_GROUPS: Array<{
  id: string;
  title: string;
  filters: PatientOpFilter[];
}> = [
  {
    id: "docs_status",
    title: "Status dos documentos",
    filters: ["docs_pending", "docs_rejected", "docs_to_approve"],
  },
  {
    id: "releases",
    title: "Liberações",
    filters: ["awaiting_release", "docs_released"],
  },
  {
    id: "purchase",
    title: "Concluiu a compra",
    filters: ["purchase_completed"],
  },
  {
    id: "all",
    title: "Transações",
    filters: ["all"],
  },
];

export const PATIENT_OP_FILTER_OPTIONS: Array<{
  id: PatientOpFilter;
  label: string;
  description: string;
}> = [
  {
    id: "docs_pending",
    label: "Pendentes",
    description: "Transação com pelo menos um documento obrigatório faltando.",
  },
  {
    id: "docs_rejected",
    label: "Recusado",
    description: "Pelo menos um documento ativo recusado pela loja.",
  },
  {
    id: "docs_to_approve",
    label: "Aguardando aprovação",
    description: "Documento enviado, aguardando verificação da loja.",
  },
  {
    id: "awaiting_release",
    label: "Aguardando liberação",
    description:
      "Todos os documentos verificados, sem liberação válida para dispensação.",
  },
  {
    id: "docs_released",
    label: "Documentos liberados",
    description:
      "Documentos validados e liberação de compra/dispensação ativa e válida.",
  },
  {
    id: "purchase_completed",
    label: "Concluiu a compra",
    description:
      "Transação cuja liberação (MedicationDispense) já foi toda consumida (quantidade zerada).",
  },
  {
    id: "all",
    label: "Transações",
    description: "Todas as transações dos pacientes com consentimento.",
  },
];

export function matchesTransactionOpFilter(
  row: DocumentTransactionRow,
  filter: PatientOpFilter,
): boolean {
  if (filter === "all") return true;
  return row.opState === filter;
}

/** @deprecated Preferir matchesTransactionOpFilter (lista por transação). */
export function matchesPatientOpFilter(
  patient: Patient,
  filter: PatientOpFilter,
  rows?: DocumentTransactionRow[],
): boolean {
  if (filter === "all") return true;
  if (!rows?.length) return false;
  const key = ecommercePatientReference(patient);
  return rows.some(
    (row) =>
      ecommercePatientReference(row.patient) === key && row.opState === filter,
  );
}

export function isDocDependentFilter(filter: PatientOpFilter): boolean {
  return filter !== "all";
}

export function countTransactionsByOpFilter(
  rows: DocumentTransactionRow[],
  filter: PatientOpFilter,
): number {
  return rows.filter((row) => matchesTransactionOpFilter(row, filter)).length;
}

export function transactionMatchesSearch(
  row: DocumentTransactionRow,
  term: string,
  matchesPatient: (patient: Patient, search: string) => boolean,
): boolean {
  if (!term.trim()) return true;
  return matchesPatient(row.patient, term);
}

export type { TransactionOpState };
